// src/pages/Auth/Login.jsx
// -----------------------------------------------------------------------------
// Login screen. Uses the REAL backend auth flow via useAuth().login() (which
// calls authService.login -> POST /api/Auth/Login and stores the JWT).
// Also offers a one-click "Use Demo Account" button (see AuthContext.loginDemo)
// for trying the UI without a real database row.
// Split-panel design: brand panel on the left, form on the right.
// -----------------------------------------------------------------------------
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import afixLogo from '../../assets/images/afix-logo.png';
import './auth.css';

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      // Real API call -> POST /api/Auth/Login (see authService.js / AuthContext.jsx)
      await login(email, password);
      toast.success('Logged in successfully');
      navigate('/dashboard');
    } catch (err) {
      if (err.forcePasswordChange) {
        // Account exists and password is correct, but backend requires a
        // password change before continuing (IsPasswordupdated = false).
        toast.info(err.message);
        navigate(`/change-password?email=${encodeURIComponent(email)}`);
        return;
      }

      if (err.request && !err.response) {
        // The request went out but nothing came back - the API isn't
        // reachable at the configured URL/port (see vite.config.js proxy
        // target / VITE_API_BASE_URL), not a wrong-credentials case.
        setError(
          "Can't reach the AFix server. Check that the API is running and that the proxy/API URL points at the right port."
        );
      } else {
        // Prefer a real server-sent message; the backend for this endpoint
        // returns the failure reason as the response body itself
        // ("Invalid Email" / "Invalid Password"), which AuthContext turns
        // into err.message.
        const message =
          err?.response?.data?.message ||
          (typeof err?.response?.data === 'string' ? err.response.data : null) ||
          err.message ||
          'Invalid email or password';
        setError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };



  return (
    <div className="auth-screen">
      {/* ---------- Left: brand panel ---------- */}
      <div className="auth-brand-panel">
        <div className="auth-blob auth-blob-1" />
        <div className="auth-blob auth-blob-2" />

        <div className="auth-brand-content">
          <img src={afixLogo} alt="AFix logo" className="auth-brand-logo" />
          <p className="auth-sub">
            Smart ticket &amp; bug tracking for teams that ship.
          </p>

          <ul className="auth-feature-list">
            <li>✓ Real-time ticket &amp; project tracking</li>
            <li>✓ Role-based dashboards for every team</li>
            <li>✓ SLA &amp; resolution insights at a glance</li>
          </ul>
        </div>
      </div>

      {/* ---------- Right: form panel ---------- */}
      <div className="auth-form-panel">
        <div className="auth-form-box">
          <img src={afixLogo} alt="AFix" className="auth-form-logo" />

          <h2>Welcome</h2>
          <p className="subtitle">Sign in to your AFix account to continue</p>

          {error && <div className="auth-error">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="auth-password-wrap">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword((s) => !s)}
                  tabIndex={-1}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <div className="auth-row-between">
              <label className="auth-remember">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                Remember me
              </label>
              <Link to="/forgot-password">Forgot password?</Link>
            </div>

            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Signing In…' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}